import {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {SUB_ADMIN_HEADERS} from '../../../../constants/RoleConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import CreateUserFrom from '../../components/forms/CreateUserForm'
import {filterHeadersByUserRole} from '../../helper/helperFunction'
import MetaTags from '../../../../meta/MetaTags'
import Loader from '../../components/Loader'

const SubAdminListingPage = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const [userData, setUserData] = useState<any>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isInviteLoading, setIsInviteLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {currentUser} = useReduxAuth()

  // function to get data
  const fetchData = useCallback(async () => {
    setIsLoading(true)
    let response

    try {
      response = await axios.get(API_ENDPOINTS.GET_ALL_SUB_ADMINS())

      const items = response?.data?.data?.subAdmins || []
      setUserData(items)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // fetch data
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // create new user(sub-admin)
  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsInviteLoading(true)
    let response
    try {
      // create mode
      response = await axios.post(API_ENDPOINTS.INVITE_USER('sub_admin'), values)
      notifySuccess(response?.data?.message)

      // Fetch updated data and close the modal
      await fetchData()
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsInviteLoading(false)
      setIsModalOpen(false)
      resetForm()
    }
  }

  // Function to filter headers based on user permissions
  const filteredHeaders = filterHeadersByUserRole(SUB_ADMIN_HEADERS, currentUser)
  // search filters from datatables
  const [searchText, setSearchText] = useState('')
  const filteredData = useMemo(() => {
    return userData.filter((doctor: any) => {
      // Search text filter
      const searchFilter =
        searchText === '' ||
        doctor.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        doctor.email?.toLowerCase().includes(searchText.toLowerCase())

      return searchFilter
    })
  }, [userData, searchText])
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'search':
        setSearchText(value)
        break
      default:
        break
    }
  }
  const handleClearAllFilters = () => {
    setSearchText('')
  }

  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name or email',
      value: searchText,
    },
  ]

  return (
    <>
      <MetaTags title={'Sub Admin List'} />
      {isInviteLoading ? (
        <Loader />
      ) : (
        <>
          <DataTableList
            title='Sub Admin'
            columns={filteredHeaders}
            data={filteredData}
            showAddButton={currentUser?.role_id === 1}
            onAddClick={() => setIsModalOpen(!isModalOpen)}
            addButtonText="Add Admin"
            isLoading={isLoading}
            showOnlySearch={true}
            showCustomFilters={true}
            customFilters={customFilters}
            onCustomFilterChange={handleFilterChange}
            onClearAllCustomFilters={handleClearAllFilters}
          />

          {/* create user form */}
          <CreateUserFrom
            title='Invite Sub Admin'
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            isLoading={isInviteLoading}
            handleSubmit={handleSubmit}
          />
        </>
      )}
    </>
  )
}

export default SubAdminListingPage
